import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const NotificationSection = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [settings, setSettings] = useState({
    emailJobMatches: true,
    emailApplicationUpdates: true,
    emailMessages: true,
    emailNewsletter: false,
    pushJobMatches: true,
    pushApplicationUpdates: true,
    pushMessages: true,
    frequency: 'instant'
  });

  const frequencyOptions = [
    { value: 'instant', label: 'Instant' },
    { value: 'daily', label: 'Daily Digest' },
    { value: 'weekly', label: 'Weekly Summary' },
    { value: 'never', label: 'Never' }
  ];

  const handleCheckboxChange = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
            <p className="text-sm text-muted-foreground">Manage how you receive updates and alerts</p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit2"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">Email Notifications</h3>
          <div className="space-y-3">
            <Checkbox
              label="Job matches and recommendations"
              description="Receive emails when new jobs match your preferences"
              checked={settings?.emailJobMatches}
              onChange={() => handleCheckboxChange('emailJobMatches')}
              disabled={!isEditing}
            />
            <Checkbox
              label="Application status updates"
              description="Get notified about changes to your applications"
              checked={settings?.emailApplicationUpdates}
              onChange={() => handleCheckboxChange('emailApplicationUpdates')}
              disabled={!isEditing}
            />
            <Checkbox
              label="Messages from recruiters"
              description="Receive emails when recruiters send you messages"
              checked={settings?.emailMessages}
              onChange={() => handleCheckboxChange('emailMessages')}
              disabled={!isEditing}
            />
            <Checkbox
              label="Newsletter and platform updates"
              description="Stay informed about new features and career tips"
              checked={settings?.emailNewsletter}
              onChange={() => handleCheckboxChange('emailNewsletter')}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className="pt-6 border-t border-border">
          <h3 className="text-sm font-medium text-foreground mb-4">Push Notifications</h3>
          <div className="space-y-3">
            <Checkbox
              label="Job matches and recommendations"
              description="Get instant notifications for relevant jobs"
              checked={settings?.pushJobMatches}
              onChange={() => handleCheckboxChange('pushJobMatches')}
              disabled={!isEditing}
            />
            <Checkbox
              label="Application status updates"
              description="Real-time updates on your applications"
              checked={settings?.pushApplicationUpdates}
              onChange={() => handleCheckboxChange('pushApplicationUpdates')}
              disabled={!isEditing}
            />
            <Checkbox
              label="Messages from recruiters"
              description="Instant alerts for new messages"
              checked={settings?.pushMessages}
              onChange={() => handleCheckboxChange('pushMessages')}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className="pt-6 border-t border-border">
          <Select
            label="Notification Frequency"
            description="Choose how often you want to receive notifications"
            options={frequencyOptions}
            value={settings?.frequency}
            onChange={(value) => setSettings(prev => ({ ...prev, frequency: value }))}
            disabled={!isEditing}
          />
        </div>

        {isEditing && (
          <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
            <Button
              variant="default"
              iconName="Check"
              iconPosition="left"
              onClick={handleSave}
            >
              Save Changes
            </Button>
            <Button
              variant="outline"
              iconName="X"
              iconPosition="left"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSection;